'use client';
import { useState } from "react";
import Link from "next/link";
import axios from "axios";
import { Dropdown } from 'primereact/dropdown';
import Button from "@/ui/button";
import Checkbox from "@/ui/checkbox";
import styles from './styles.module.css';
import './contact-form-page.css';
import cl from "clsx";
import ModalContinue from "@/components/elements/modal-continue/modal-continue";

export default function ContactForm({ dict, showComment = true }) {
    const [captchaToken, setCaptchaToken] = useState("");

    const handleVerify = (token) => {
        console.log("reCAPTCHA token:", token);
        setCaptchaToken(token);
    };

    const {
        titles,
        descriptions,
        placeholders,
        checkboxLabels,
        links,
        buttons,
        alertMessages,
        errorMessages,
        questionOptions
    } = dict?.contactFormContactPage;

    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [questionType, setQuestionType] = useState('');
    const [message, setMessage] = useState('');
    const [isChecked, setIsChecked] = useState(false);
    const [visible, setVisible] = useState(false);
    const [attention, setAttention] = useState(false);
    const [modalError, setModalError] = useState(false);
    const [modalErrorText, setModalErrorText] = useState('');
    const [confirmPhone, setConfirmPhone] = useState('');

    const validateEmail = (email) => {
        return String(email)
            .toLowerCase()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
    };

    const validatePhone = (phone) => {
        return String(phone)
            .match(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/);
    };

    // Dropdown options for question type
    const dropdownOptions = [
        { label: questionOptions.general, value: 'General Question' },
        { label: questionOptions.technical, value: 'Technical Question' },
        { label: questionOptions.billing, value: 'Billing Question' },
    ];

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!isChecked) {
            setAttention(true);
            return;
        }

        if (!validateEmail(email)) {
            setModalErrorText(dict.contactForm?.invalidEmail || 'Please enter a valid e-mail address.');
            setModalError(true);
            return;
        }

        if (!validatePhone(phoneNumber)) {
            setModalErrorText(dict.contactForm?.invalidPhone || 'Please enter a valid phone number.');
            setModalError(true);
            return;
        }

        if (showComment && !message.trim()) {
            setModalErrorText(errorMessages.invalidComment || 'Please enter a comment.');
            setModalError(true);
            return;
        }

        try {
            const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/$/, '');
            const response = await axios.post(`${apiUrl}/api/base/contact/`, {
                name,
                email,
                phone: phoneNumber,
                question_type: questionType,
                comment: message,
                confirm_phone: confirmPhone
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'accept': 'application/json'
                }
            });

            if (response.status === 200) {
                setVisible(true);
                setName('');
                setPhoneNumber('');
                setEmail('');
                setQuestionType('');
                setMessage('');
                setIsChecked(false);
                setConfirmPhone('');
            } else {
                setModalErrorText(errorMessages.submitError);
                setModalError(true);
            }
        } catch (error) {
            console.error("Error sending message:", error);
            setModalErrorText(errorMessages.generalError);
            setModalError(true);
        }
    };

    return (
        <form className={cl(styles.contact_form, 'contact-form-page')} onSubmit={handleSubmit} data-name="contact-form-contact-page">
            {/*<ReCaptchaWrapper onVerify={handleVerify} />*/}
            <div>
                <h1 className={styles.contact_form__title}>{titles.mainTitle}</h1>
                <p className={styles.contact_form__descriptions}>{descriptions.mainDescription}</p>
            </div>

            <div className={styles.contact_form_wrapper}>
                <div className={styles.contact_form__row}>
                    <input
                        id="name"
                        className={styles.contact_form__input}
                        type="text"
                        placeholder={placeholders.name}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />
                    <input
                        id="email"
                        className={styles.contact_form__input}
                        type="email"
                        placeholder={placeholders.email}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>
                <div className={styles.contact_form__row}>
                    <input
                        id="phone"
                        className={styles.contact_form__input}
                        type="tel"
                        placeholder={placeholders.phoneNumber}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        required
                    />
                    <div className="extra_contact_row">
                        <input
                            type="text"
                            name="confirm_phone"
                            className={styles.contact_form__input}
                            placeholder={placeholders.phoneNumber}
                            value={confirmPhone}
                            onChange={(e) => setConfirmPhone(e.target.value)}
                            autoComplete="off"
                            tabIndex={-1}
                        />
                    </div>
                    <Dropdown
                        id="question-type"
                        value={questionType}
                        options={dropdownOptions}
                        onChange={(e) => setQuestionType(e.value)}
                        placeholder={placeholders.questionType}
                        className={styles.contact_form__dropdown}
                    />
                </div>
                {showComment && (
                    <textarea
                        id="message"
                        className={styles.contact_form__textarea}
                        placeholder={placeholders.comment}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                    />
                )}
                <div className={styles.contact_form__checkbox_block_mobile}>
                    <Checkbox
                        checked={isChecked}
                        onChange={() => setIsChecked(!isChecked)}
                    />
                    <label htmlFor="contact-form-checkbox">
                        {checkboxLabels.consentMobile}
                        <Link href="https://ic-ces.at/compliance-policy" className={styles.contact_form__link}>
                            {links.termsAndConditions}
                        </Link>.
                    </label>
                </div>
                <div className={styles.contact_form__button_row}>
                    <div className={styles.contact_form__button_wrapper}>
                        <Button
                            typeButton="submit"
                            type="submit"
                            title={buttons.contactUs}
                            // disabled={!captchaToken}
                        >
                            {buttons.submit}
                        </Button>
                    </div>
                    <div className={styles.contact_form__checkbox_block}>
                        <Checkbox
                            checked={isChecked}
                            onChange={() => setIsChecked(!isChecked)}
                        />
                        <label htmlFor="contact-form-checkbox">
                            {(() => {
                                const [before, after] = checkboxLabels.consentDesktop.split('{link}');
                                return (
                                    <>
                                        {before}
                                        <Link href="https://ic-ces.at/compliance-policy" target="_blank" className={styles.contact_form__link}>
                                            {checkboxLabels.consentDesktopLink}
                                        </Link>
                                        {after}
                                    </>
                                );
                            })()}
                        </label>
                    </div>
                </div>
            </div>
            <ModalContinue
                visible={visible}
                setVisible={setVisible}
                title={titles.thankYou}
                text={descriptions.modalThankYouText}
                dict={dict}
            />
            <ModalContinue
                visible={attention}
                setVisible={setAttention}
                title={titles.termsAndConditions}
                text={
                    <>
                        {descriptions.modalTermsText}
                        <Link
                            href="https://ic-ces.at/en/datenschutz/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={'link'}
                        >
                            {links.privacyPolicy}
                        </Link>
                        .
                    </>
                }
                dict={dict}
            />
            <ModalContinue
                visible={modalError}
                setVisible={setModalError}
                title={titles.error || 'Error'}
                text={modalErrorText}
                dict={dict}
            />
        </form>
    );
}
